import { Globe, ChevronDown } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { LANGS, type Lang } from "@/lib/i18n";
import { trackEvent } from "@/lib/audit";
import { useMounted } from "@/hooks/useMounted";

/**
 * Compact language dropdown — high contrast, always-visible label.
 * Uses a native <select> for full mobile/keyboard compatibility.
 */
export function LanguageDropdown({ className = "" }: { className?: string }) {
  const { lang, setLang, t } = useLanguage();
  // QA-FINAL-02 — avoid SSR↔client hydration mismatch when the stored
  // language differs from the SSR default ("ES"). Render the SSR value
  // until after mount, then reveal the real selected language.
  const mounted = useMounted();
  const displayLang: Lang = mounted ? lang : "ES";
  const label = (l: Lang) =>
    l === "ES" ? t("language_es") : l === "EN" ? t("language_en") : t("language_pt");
  return (
    <label
      className={
        "relative inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background/90 px-2.5 py-1.5 text-[12px] font-bold text-foreground shadow-sm backdrop-blur transition hover:border-primary/60 hover:bg-background " +
        className
      }
    >
      <Globe className="h-3.5 w-3.5 text-primary" aria-hidden />
      <span className="sr-only">{t("language_dropdown_label")}</span>
      <span aria-hidden className="pointer-events-none font-black tracking-wider text-foreground">
        {displayLang}
      </span>
      <ChevronDown className="h-3 w-3 text-muted-foreground" aria-hidden />
      <select
        value={displayLang}
        aria-label={t("language_dropdown_label")}
        onChange={(e) => {
          const next = e.target.value as Lang;
          setLang(next);
          void trackEvent("language_dropdown_used", { lang: next });
        }}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent text-transparent opacity-0 outline-none"
      >
        {LANGS.map((l) => (
          <option key={l} value={l} className="bg-background text-foreground">
            {l} · {label(l)}
          </option>
        ))}
      </select>
    </label>
  );
}